
"use client";
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import newsletterBird from "@/assets/newsletter-bird.svg";

function BackgroundBeamsDemo() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save email to the newsletter_subscribers table
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert([
          { 
            email, 
            source: 'homepage_newsletter',
            metadata: { 
              timestamp: new Date().toISOString(),
              page: 'home' 
            }
          }
        ]);
      
      if (error) {
        if (error.code === '23505') { // Unique violation error code
          toast.info("You're already subscribed to our newsletter!");
        } else {
          console.error("Submission error:", error);
          throw error;
        }
      } else {
        // Clear the input after successful submission
        setEmail("");
        toast.success("Thanks for subscribing to our newsletter! 📧");
        
        // Send confirmation email using Supabase Edge Function
        try {
          console.log("Invoking edge function with email:", email);
          
          // Use supabase client to invoke the edge function instead of direct fetch
          const { data: functionData, error: functionError } = await supabase.functions.invoke(
            'newsletter-confirmation',
            {
              body: JSON.stringify({ 
                email,
                source: 'homepage_newsletter', 
                timestamp: new Date().toISOString(),
              })
            }
          );
          
          if (functionError) {
            console.error("Edge function error:", functionError);
          } else {
            console.log("Edge function response:", functionData);
          }
        } catch (emailError) {
          console.error("Email sending error:", emailError);
          // We don't show this error to the user since their signup was successful
        }
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="newsletter" className="w-full bg-background py-20 md:py-28">
      <div className="mx-auto flex max-w-3xl flex-col items-center px-4 text-center">
        <img src={newsletterBird} alt="" className="mb-6 h-24 w-auto md:h-32" />
        <h2 className="font-display text-3xl text-quickatoo-purple md:text-5xl">
          Join the Newsletter
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-sm text-quickatoo-charcoal/80 md:text-base">
          Stay updated with the latest Quickatoo features, painting industry insights, and exclusive tips.
          Be the first to experience Quickatoo, the revolutionary tool for painting professionals.
          Our platform helps you create accurate quotes in minutes, not hours.
          Get early access to streamline your workflow, win more jobs, and grow your painting business.
        </p>
        <form onSubmit={handleSubmit} className="mt-10 flex w-full max-w-2xl flex-col gap-3 md:flex-row">
          <Input
            type="email"
            placeholder="info@quickatoo.com"
            className="h-[52px] w-full rounded-2xl border-quickatoo-charcoal/20 px-6 text-base md:h-[62px]"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            className="h-[52px] w-full rounded-2xl bg-quickatoo-yellow px-8 text-lg font-semibold text-quickatoo-charcoal hover:bg-quickatoo-yellow/90 md:h-[62px] md:w-auto"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Join Newsletter"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export { BackgroundBeamsDemo };
